/**
 * EditSession — the BE-issued lease that turns a chat-attached artefact into
 * an editable surface. Mirrors the response of `POST /api/v1/edit-sessions`
 * intentionally inline so this module stays *zero-dep* on `@ando/core` —
 * the editor primitives ship inside `@ando/ui` and must be usable from any
 * app (chat, drive, drawer, mobile) without dragging the full domain graph.
 *
 * Why a lease (not just a URL):
 *  - `etag` lets the saver short-circuit a no-op PUT and gives us optimistic
 *    concurrency against another tab editing the same file.
 *  - `lock_token` keeps a multi-author Drive scenario sane (BE rejects PUTs
 *    that don't carry the active token).
 *  - `expires_at` is the renew trigger — the editor refreshes the lease
 *    before saving when within 5min of expiry. Local IndexedDB drafts
 *    survive lease expiry, so unsaved work is never lost on tab sleep.
 *  - `save_method` is dynamic because Drive (PUT to MinIO presigned) and
 *    SharePoint-style backends (POST to chunked endpoint) diverge.
 */
/**
 * EditSession action-state — populated when the document is a pending AI
 * draft (Phase 1.1 BE work, "Editor-first canvas", May 2026). Drives the
 * inline ``<DraftActionBar>`` rendered above the editor. Mirrored verbatim
 * from ``@ando/core::EditSessionActionState`` — the UI package stays
 * zero-dep on ``@ando/core`` so apps that compile the editors standalone
 * don't drag in the domain graph. The mirror check in
 * ``apps/client/src/features/workspace/hooks/useEditSession.ts`` trips
 * ``tsc --noEmit`` if the two shapes drift.
 */
export interface EditSessionActionState {
  readonly proposal_id: string;
  readonly draft_status: "pending_ai" | "committed" | "rejected";
  readonly rationale: string;
  readonly selected_destination_id: string;
  readonly destinations: ReadonlyArray<{
    readonly id: string;
    readonly label: string;
    readonly icon?: string | null;
    readonly disabled?: boolean;
  }>;
}

export interface EditSession {
  /** Local logical session — also the IndexedDB key. Survives reloads. */
  session_id: string;
  /** Canonical doc identifier on the BE (drive item id / chat attachment id). */
  doc_id: string;
  /** MIME — drives which editor we mount (pdf vs docx vs xlsx). */
  mime_type: string;
  /** Display name for the editor chrome. */
  filename: string;
  /** Signed GET URL to fetch the original bytes (MinIO / SharePoint). */
  read_url: string;
  /** Signed mutator URL the editor PUTs/POSTs the saved blob to. */
  save_url: string;
  /** Verb to use against `save_url`. */
  save_method: "PUT" | "POST";
  /**
   * S3/MinIO ETag of the version we leased — passed back as `If-Match` on
   * save. Nullable because some object stores (or the very first version
   * of a freshly minted artefact before MinIO indexes it) legitimately
   * return no ETag; in that case the editor saves without optimistic
   * concurrency and trusts the BE to settle conflicts.
   */
  etag: string | null;
  /** Optional cooperative lock — present in multi-author scenarios. */
  lock_token: string | null;
  /** ISO-8601 lease expiry — editor renews before save if past. */
  expires_at: string;
  /**
   * Optional action-state when this document is still a pending AI draft.
   * See {@link EditSessionActionState}. ``null`` / ``undefined`` for every
   * regular (non-AI-drafted) document — the ``<DraftActionBar>`` skips
   * mounting in that case.
   */
  action_state?: EditSessionActionState | null;
}

/**
 * Minimal READ-ONLY session around an already-fetched ``blob:`` URL (or any
 * directly fetchable URL). The inline viewers (``PDFViewerInline`` /
 * ``ImageViewerInline``) only read ``read_url`` / ``filename`` /
 * ``mime_type``; the save-side fields are inert. Shared by every surface
 * that mounts a viewer WITHOUT a BE edit lease — the Hive-flow run document
 * pane and the Tecnico hub's PDF-tavola stage (Sep 2026).
 */
export function readOnlyBlobSession(readUrl: string, filename: string, mime: string): EditSession {
  return {
    session_id: `ro-${filename}`,
    doc_id: `ro-${filename}`,
    mime_type: mime,
    filename,
    read_url: readUrl,
    save_url: "",
    save_method: "PUT",
    etag: null,
    lock_token: null,
    expires_at: "",
  };
}
